import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Flex, Modal, Spin, Tooltip, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { CustomButton } from '@/components';
import SearchInput from '@/components/SearchInput';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { useToken } from '@/common/helper';
import { MassActionType, shipmentMassActions } from '@/apis/shipment';
import type { TeamDDLOption } from '@/apis/lookup';
import {
  coverageFromCount,
  getTeamCheckboxState,
  getTeamStatusLabel,
  nextTeamIntent,
  sortOnSelectedTeams,
  type EditTeamOption,
  type TeamIntent,
} from './editTeamsModalState';
import { splitCalendarScopedFilters } from './utils';
import './EditTeamsModal.css';

type PreflightTeamRow = {
  id: string;
  count: number;
  name?: string;
  color?: string;
};

type PreflightPayload = {
  total?: number;
  shipment_labels?: string[];
  teams?: PreflightTeamRow[];
};

const unwrapPreflight = (preflight: unknown): PreflightPayload => {
  const root = preflight as {
    data?: { data?: PreflightPayload & { data?: PreflightPayload } };
  };
  const inner = root?.data?.data;
  if (inner?.data && (inner.data.teams || inner.data.total != null)) {
    return inner.data;
  }
  if (inner && (inner.teams || inner.total != null)) {
    return inner;
  }
  return {};
};

interface EditTeamsModalProps {
  open: boolean;
  onCancel: () => void;
  onApply: (payload: {
    add_team_ids: string[];
    remove_team_ids: string[];
  }) => void;
  selectedIds: string[];
  selectedCount: number;
  selectAllAcrossFilters?: boolean;
  excludedIds?: string[];
  filtersPayload?: Record<string, unknown>;
  appliedSearchQuery?: string;
}

const EMPTY_IDS: string[] = [];
const EMPTY_FILTERS: Record<string, unknown> = {};

const EditTeamsModal: React.FC<EditTeamsModalProps> = ({
  open,
  onCancel,
  onApply,
  selectedIds,
  selectedCount,
  selectAllAcrossFilters = false,
  excludedIds = EMPTY_IDS,
  filtersPayload = EMPTY_FILTERS,
  appliedSearchQuery = '',
}) => {
  const { token } = useToken();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(selectedCount);
  const [labels, setLabels] = useState<string[]>([]);
  const [teams, setTeams] = useState<EditTeamOption[]>([]);
  const [intents, setIntents] = useState<Record<string, TeamIntent>>({});

  const resetState = useCallback(() => {
    setSearch('');
    setIntents({});
    setTeams([]);
    setLabels([]);
    setTotal(selectedCount);
  }, [selectedCount]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const { filters, start, end, view_by } =
      splitCalendarScopedFilters(filtersPayload);
    const preflightPayload = selectAllAcrossFilters
      ? {
          action: 'update_teams_preflight' as MassActionType,
          select_all: true,
          exclude_shipment_ids: excludedIds,
          filters: {
            ...filters,
            search: (appliedSearchQuery ?? '').trim(),
          },
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
          ...(view_by ? { view_by } : {}),
        }
      : {
          action: 'update_teams_preflight' as MassActionType,
          shipment_ids: selectedIds,
        };

    try {
      const [ddl, preflight] = await Promise.all([
        getTeamsDDLOFFeature(token || '', 'Shipment'),
        shipmentMassActions(token || '', preflightPayload),
      ]);
      const ddlTeams: TeamDDLOption[] = Array.isArray(ddl) ? ddl : [];
      const data = unwrapPreflight(preflight);
      const preflightTeams = data.teams || [];
      const countById = new Map(
        preflightTeams.map((row) => [String(row.id), row])
      );
      const resolvedTotal = data.total ?? selectedCount;
      setTotal(resolvedTotal);
      setLabels(
        Array.isArray(data.shipment_labels) ? data.shipment_labels : []
      );
      const fromDdl: EditTeamOption[] = ddlTeams.map((team) => {
        const row = countById.get(String(team.id));
        const count = row?.count ?? 0;
        return {
          id: String(team.id),
          name: team.name,
          color: team.color,
          count,
          coverage: coverageFromCount(count, resolvedTotal),
          canUpdate: team.permissions?.is_update !== false,
        };
      });
      const ddlIds = new Set(fromDdl.map((team) => team.id));
      const extras: EditTeamOption[] = preflightTeams
        .filter((row) => !ddlIds.has(String(row.id)))
        .map((row) => {
          const count = row.count ?? 0;
          return {
            id: String(row.id),
            name: row.name || 'Unknown team',
            color: row.color,
            count,
            coverage: coverageFromCount(count, resolvedTotal),
            canUpdate: false,
          };
        });
      setTeams([...fromDdl, ...extras]);
      setIntents({});
    } catch {
      message.error('Could not load team assignments');
    } finally {
      setLoading(false);
    }
  }, [
    token,
    selectedIds,
    selectedCount,
    selectAllAcrossFilters,
    excludedIds,
    filtersPayload,
    appliedSearchQuery,
  ]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    void loadData();
    // Fetch once when the modal opens; ignore parent identity churn while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const query = search.trim().toLowerCase();
  const visibleTeams = useMemo(
    () =>
      query
        ? teams.filter((team) => team.name.toLowerCase().includes(query))
        : teams,
    [teams, query]
  );

  const onSelected = useMemo(() => {
    const rows = visibleTeams.filter((team) => {
      const intent = intents[team.id] ?? 'unchanged';
      return team.coverage !== 'none' || intent === 'add';
    });
    return sortOnSelectedTeams(rows, intents);
  }, [visibleTeams, intents]);
  const allTeams = visibleTeams.filter((team) => {
    const intent = intents[team.id] ?? 'unchanged';
    return team.coverage === 'none' && intent !== 'add';
  });

  const pendingAdds = teams.filter(
    (team) => (intents[team.id] ?? 'unchanged') === 'add'
  );
  const pendingRemoves = teams.filter(
    (team) => (intents[team.id] ?? 'unchanged') === 'remove'
  );
  const hasChanges = pendingAdds.length > 0 || pendingRemoves.length > 0;

  const toggleTeam = (team: EditTeamOption) => {
    if (!team.canUpdate) return;
    setIntents((prev) => ({
      ...prev,
      [team.id]: nextTeamIntent(team.coverage, prev[team.id] ?? 'unchanged'),
    }));
  };

  const extraLabelCount = Math.max(0, total - labels.length);
  const tooltipContent =
    labels.length === 0 && extraLabelCount === 0 ? null : (
      <div className='edit-teams-id-tooltip'>
        <ul>
          {labels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
        {extraLabelCount > 0 ? <div>and {extraLabelCount} more</div> : null}
      </div>
    );

  const renderRow = (team: EditTeamOption) => {
    const intent = intents[team.id] ?? 'unchanged';
    const box = getTeamCheckboxState(team.coverage, intent);
    const status = getTeamStatusLabel(team.coverage, intent, team.count, total);
    return (
      <div
        key={team.id}
        className={`edit-teams-modal-row${team.canUpdate ? '' : ' is-disabled'}`}
        onClick={() => toggleTeam(team)}
        role='button'
        tabIndex={team.canUpdate ? 0 : -1}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleTeam(team);
          }
        }}
      >
        <span onMouseDown={(event) => event.preventDefault()}>
          <Checkbox
            checked={box.checked}
            indeterminate={box.indeterminate}
            disabled={!team.canUpdate}
            tabIndex={-1}
            onClick={(event) => event.stopPropagation()}
            onChange={() => toggleTeam(team)}
          />
        </span>
        <span
          className='edit-teams-modal-dot'
          style={{ backgroundColor: team.color || '#000' }}
        />
        <span className='edit-teams-modal-name'>{team.name}</span>
        {status ? (
          <span className={`edit-teams-modal-status is-${status.tone}`}>
            {status.text}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      centered
      zIndex={2000}
      width={480}
      className='edit-teams-modal custom-delete-modal'
      wrapClassName='edit-teams-modal-wrap'
      classNames={{
        wrapper: 'edit-teams-modal-wrap',
        container: 'edit-teams-modal-container',
        body: 'edit-teams-modal-antd-body',
      }}
      styles={{ wrapper: { overflow: 'hidden' } }}
      destroyOnHidden
    >
      <div className='edit-teams-modal-header'>
        <h3 className='edit-teams-modal-title'>Edit teams</h3>
        <CloseOutlined className='edit-teams-modal-close' onClick={onCancel} />
      </div>
      <p className='edit-teams-modal-subtitle'>
        Changes apply to{' '}
        {tooltipContent ? (
          <Tooltip title={tooltipContent}>
            <span className='edit-teams-modal-subtitle-count'>
              {total} selected shipment{total === 1 ? '' : 's'}
            </span>
          </Tooltip>
        ) : (
          `${total} selected shipment${total === 1 ? '' : 's'}`
        )}
      </p>
      <div className='edit-teams-modal-search'>
        <SearchInput
          placeholder='Search teams'
          width='100%'
          defaultValue={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
        />
      </div>
      {loading ? (
        <Flex justify='center' style={{ padding: '32px 0' }}>
          <Spin />
        </Flex>
      ) : (
        <div className='edit-teams-modal-body'>
          {onSelected.length > 0 ? (
            <>
              <p className='edit-teams-modal-section-title'>
                On selected shipments
              </p>
              {onSelected.map(renderRow)}
            </>
          ) : null}
          {onSelected.length > 0 && allTeams.length > 0 ? (
            <hr className='edit-teams-modal-divider' />
          ) : null}
          {allTeams.length > 0 ? (
            <>
              <p className='edit-teams-modal-section-title'>All teams</p>
              {allTeams.map(renderRow)}
            </>
          ) : null}
          {onSelected.length === 0 && allTeams.length === 0 ? (
            <p className='edit-teams-modal-empty'>
              {query
                ? 'No teams match this search'
                : 'No teams are available to assign'}
            </p>
          ) : null}
        </div>
      )}
      <div className='edit-teams-modal-footer'>
        <button
          type='button'
          onClick={onCancel}
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
            height: 40,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <CustomButton
          text='Apply'
          height={40}
          bgColor='#000'
          color='#fff'
          fontSize='14px'
          fontWeight={600}
          borderRadius='8px'
          padding='10px 16px'
          disabled={!hasChanges || loading}
          onClick={() =>
            onApply({
              add_team_ids: pendingAdds.map((team) => team.id),
              remove_team_ids: pendingRemoves.map((team) => team.id),
            })
          }
        />
      </div>
    </Modal>
  );
};

export default EditTeamsModal;
